import {
  Box,
  Divider,
  Button,
  HStack,
  Stack,
  Text,
  Tooltip,
  useColorModeValue,
  Spacer,
  useToast,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useState } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  ThreadGQLType,
  CommentGQLType,
  useCreateNewCommentMutation,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { colors } from 'packages/common-frontend/colors';
import {
  RenderStaticHtml,
  RichTextEditor,
} from 'packages/common-frontend/components/RichTextEditor/RichTextEditor';
import { errorToastConfig } from 'packages/common-frontend/constants';

const USER_COLORS = [
  '#CBD5E0',
  '#F6E05E',
  '#4FD1C5',
  '#76E4F7',
  '#B794F4',
  '#F687B3',
];

// deterministic way to get an int from a string
// used with modulo to find a random but deterministic color for a user
function simpleStringHash(s: string) {
  let result = 9;
  let index = 0;
  // eslint-disable-next-line no-restricted-syntax
  while (index < s.length) {
    // eslint-disable-next-line no-bitwise
    result = Math.imul(result ^ s.charCodeAt(index), 9 ** 9);
    index += 1;
  }
  // eslint-disable-next-line no-bitwise
  return result ^ (result >>> 9);
}

const CommentBox = ({
  comment,
  children = <></>,
}: {
  comment: CommentGQLType;
  children?: React.ReactNode;
}) => {
  const {
    authorFirstName,
    authorLastName,
    authorOrganizationSlug,
    created_at,
    comment: commentText,
  } = comment;
  const initials = authorFirstName.charAt(0) + authorLastName.charAt(0);

  const textColor = useColorModeValue('gray.600', 'gray.200');

  const index =
    Math.abs(simpleStringHash(comment.authorUserId)) % USER_COLORS.length;
  const circleColor = USER_COLORS[index];

  return (
    <Stack>
      <HStack alignItems="flex-start">
        <Box borderRadius="50%" padding="8px" bgColor={circleColor}>
          <Text>{initials.toUpperCase()}</Text>
        </Box>
        <Stack spacing="4px">
          <Text fontSize="small" fontWeight="bold">{`${_.startCase(
            authorFirstName,
          )} ${_.startCase(authorLastName)}, ${_.startCase(
            authorOrganizationSlug,
          )}`}</Text>
          <Text fontSize="small" color={textColor}>
            {commonDate.epochToLocaleDateString(created_at)}
          </Text>
        </Stack>
        <Spacer />
        {children}
      </HStack>
      <Box mt={2} ml={4}>
        <RenderStaticHtml textHtml={commentText} color={textColor} />
      </Box>
    </Stack>
  );
};

export const CommentThread = ({
  thread,
  organizationId,
  userId,
  refetchData = () => {},
  children = <></>,
}: {
  thread: ThreadGQLType;
  organizationId: string;
  userId: string;
  refetchData?: () => void;
  children?: React.ReactNode;
}) => {
  const toast = useToast();
  const [showWholeThread, setShowWholeThread] = useState<boolean>(false);
  const [reply, setReply] = useState<string>('');

  const textColor = useColorModeValue('gray.600', 'gray.200');
  const bgColor = useColorModeValue(colors.background[50], 'gray.800');

  const allOrganizationIds = thread.threadPermissions.map(
    (tp) => tp.organizationId,
  );

  const [createNewCommentMutation] = useCreateNewCommentMutation({
    fetchPolicy: 'network-only',
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });

  if (
    thread.comments.length === 0 ||
    !allOrganizationIds.includes(organizationId)
  ) {
    return <></>;
  }
  const firstComment = thread.comments[0];
  const restOfTheComments = thread.comments.slice(1);

  const externalOrganizations = thread.threadPermissions.filter(
    (mapping) => mapping.organizationId !== organizationId,
  );
  const externalOrganizationNames = externalOrganizations
    .map((o) => _.startCase(o.organizationSlug))
    .join(', ');

  return (
    <Box borderRadius="8px" borderWidth="1px" padding="16px" bgColor={bgColor}>
      {externalOrganizations.length > 0 && (
        <Tooltip label={externalOrganizationNames} placement="top-start">
          <Box
            borderRadius="8px"
            bgColor="teal"
            color="white"
            fontSize="small"
            padding="8px"
          >
            {`${externalOrganizations.length} External ${
              externalOrganizations.length > 1 ? 'Parties' : 'Party'
            }`}
          </Box>
        </Tooltip>
      )}
      {children}
      <Box my={4}>
        <CommentBox comment={firstComment} />
      </Box>
      <Divider />
      <HStack mt={2} mb={0}>
        <Spacer />
        {restOfTheComments.length > 0 && !showWholeThread && (
          <Button
            size="sm"
            variant="link"
            textDecoration="none"
            color={textColor}
            onClick={() => {
              setShowWholeThread(true);
            }}
          >
            {restOfTheComments.length === 1
              ? '1 comment'
              : `${restOfTheComments.length} comments`}
          </Button>
        )}
      </HStack>
      <Stack spacing="12px">
        {showWholeThread &&
          restOfTheComments.map((c, idx) => {
            const mt = idx === 0 ? 0 : 4;
            return (
              <Box key={c.id} mt={mt} width="full">
                <Box mt={2}>
                  <CommentBox comment={c}>
                    {idx === 0 ? (
                      <HStack alignContent="flex-end">
                        <Text fontSize="sm" color={textColor}>
                          {restOfTheComments.length === 1
                            ? '1 comment'
                            : `${restOfTheComments.length} comments`}
                        </Text>
                        <Button
                          size="sm"
                          variant="link"
                          textDecoration="none"
                          color={textColor}
                          onClick={() => {
                            setShowWholeThread(false);
                          }}
                        >
                          Hide
                        </Button>
                      </HStack>
                    ) : (
                      <></>
                    )}
                  </CommentBox>
                </Box>
              </Box>
            );
          })}
      </Stack>
      <Box width="full" mt={6}>
        <RichTextEditor
          text={reply}
          setText={setReply}
          placeHolderText="Leave a comment"
          isEditable
        />
        <HStack mt={4}>
          <Spacer />
          <Button
            py={4}
            px={5}
            justifyContent="flex-start"
            variant="solid"
            _focus={{ boxShadow: 'none' }}
            size="sm"
            colorScheme="aurora"
            color="white"
            onClick={async () => {
              await createNewCommentMutation({
                variables: {
                  threadId: thread.threadId,
                  comment: reply,
                  authorUserId: userId,
                },
              });
              refetchData();
              setReply('');
            }}
          >
            Reply
          </Button>
        </HStack>
      </Box>
    </Box>
  );
};
